import React from 'react';
import PropTypes from 'prop-types';
import logger from '../../utils/logger.js';

const boundaryLogger = logger.child('ErrorBoundary');

/**
 * ErrorBoundary (#370)
 *
 * The recovery layer this app was missing: before it, a render crash in any
 * widget (market card, editor, preview) white-screened the whole financial
 * app, and a rejected React.lazy chunk load had no way back.
 *
 * Usage:
 *   <ErrorBoundary>                      — full-page (App root)
 *   <ErrorBoundary variant="section">    — route/pane level (keeps the nav)
 *   <ErrorBoundary fallback={<X/>}>      — custom fallback
 */
const isChunkLoadError = (error) => {
  const message = String(error?.message || error || '');
  return (
    error?.name === 'ChunkLoadError' ||
    /failed to fetch dynamically imported module|loading chunk|import\(\) failed/i.test(message)
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReset = this.handleReset.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    boundaryLogger.error('Render crash caught', {
      boundary: this.props.name || this.props.variant,
      error: error?.message || String(error),
      componentStack: errorInfo?.componentStack,
    });
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch {
        // an error handler must never re-crash the boundary
      }
    }
  }

  handleReset() {
    this.setState({ error: null });
    if (this.props.onReset) {
      try {
        this.props.onReset();
      } catch {
        // ignore
      }
    }
  }

  handleReload() {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children ?? null;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const chunkError = isChunkLoadError(error);
    const isSection = this.props.variant === 'section';

    return (
      <div
        role="alert"
        className={
          isSection
            ? 'm-6 rounded-lg border border-red-200 bg-red-50 p-6'
            : 'min-h-screen flex items-center justify-center bg-gray-50 p-6'
        }
      >
        <div className={isSection ? '' : 'max-w-md w-full bg-white rounded-lg shadow p-8 text-center'}>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Something went wrong
          </h2>
          {chunkError ? (
            <p className="text-sm text-gray-600 mb-4">
              A new version of the app may have been deployed. Reload the page
              to get the latest version.
            </p>
          ) : (
            <p className="text-sm text-gray-600 mb-4">
              This section hit an unexpected error. Your data is safe — you can
              try again, or reload the page if the problem persists.
            </p>
          )}
          <div className={`flex gap-3 ${isSection ? '' : 'justify-center'}`}>
            {!chunkError && (
              <button
                type="button"
                onClick={this.handleReset}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                Try again
              </button>
            )}
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
  variant: PropTypes.oneOf(['page', 'section']),
  name: PropTypes.string,
  onError: PropTypes.func,
  onReset: PropTypes.func,
};

export default ErrorBoundary;
